// src/utils/sortLeasesColumn.js
//
// LSE-LEASES-LIST-DETAIL-NAV-RESPECTS-LIST-SORT — Phase 0 Audit close-report
// ──────────────────────────────────────────────────────────────────────────
// FINDINGS:
//   LeaseSummaryTab (src/features/leases/LeaseSummaryTab.jsx):
//     No Previous/Next arrows exist. The component accepts (locationId,
//     canWrite, locationCity, leaseEditOpen, onLeaseEditChange). Which lease to
//     show: server GET /api/locations/:locId/lease uses ORDER BY created_at DESC
//     LIMIT 1. For multi-lease locations a new overrideLeaseId prop is needed.
//
//   leasePortfolioSort.js:
//     Exports smartSortComparator (STATUS_PRIORITY + resolvedEndMs),
//     applyLeaseSort (dropdown sort: smart/endDate/annualCost/daysRemaining).
//     Both are reused here rather than duplicated.
//
//   portfolioStatePersistence.js:
//     Existing keys: ripple_leases_filters_v1 (loadLeasesFilters),
//     ripple_leases_sort_v1 (loadLeasesSort: {sortKey, leaseSortKey,
//     leaseSortDir, sortMode}), ripple_leases_view_v1 (loadLeasesView).
//     No navigation-context field exists — adding in this task.
//
//   App.jsx:
//     deriveNavLocations at line 118. No navigation-context field or lease-
//     nav useMemo exists. No ?from= URL param is in use (state-based nav).
//     Decision: use sessionStorage lastListView key (per Phase 4 brief).
//
// ──────────────────────────────────────────────────────────────────────────
//
// Shared column-sort helpers for the Leases list table and the nav derivation
// in App.jsx (deriveNavLeases). Extracted from LeasesListTable.jsx so both
// surfaces use the same comparator and can never drift.
//
// Mirrors the shape of src/utils/sortLocationsColumn.js.
//
// sortValue     — extract a comparable primitive from a lease row for a given
//                 sort key. Mirrors the column definitions in LeasesListTable's
//                 COLUMNS array (location / region / annual_cost / end_date).
// applyColumnSort — sort a lease array by (sortKey, sortDir) in-place-safe
//                 (returns a new array). No-ops when sortKey is falsy.

import { resolveTimelineEndAnchor, parseDateOnly } from "../features/leases/leaseTabConfig.js";
import { getMAndATagName } from "./mAndA.js";
import { LEASE_STATUS_SORT_ORDER } from "../features/leases/leasePortfolioSort.js";
import { statusFor } from "../features/leases/pages/components/LeasePortfolioFilters.jsx";

export function sortValue(lease, key) {
  switch (key) {
    case "location":    return (lease.city || "").toLowerCase();
    case "region":      return lease.region || "";
    // LEASE-COST-FX-FIX-S1: display column shows annualRentUsd (FX-converted
    // USD). Sort key must match or rows order by unconverted native magnitudes.
    // Null rows land last — handled via early-exit in applyColumnSort.
    case "annual_cost": return lease.annualRentUsd ?? null;
    case "end_date": {
      // Sort purely by date value; rows with no date sort last regardless of status.
      const anchor = resolveTimelineEndAnchor(lease);
      if (!anchor.date) return Infinity;
      const d = parseDateOnly(anchor.date);
      return d ? d.getTime() : Infinity;
    }
    // LSE-LANDING-STATUS-SORT-ORDER: numeric index so ASC = Draft → In-Use →
    // Vacated → Sublease-Out → Expired, matching the Status filter dropdown order.
    // Mirrors the same case in LeasesListTable's internal sortValue().
    case "status": return LEASE_STATUS_SORT_ORDER[statusFor(lease)] ?? 99;
    // For m_and_a, return the display tag name for tagged rows; no-tag rows use
    // a null-last path handled in applyColumnSort (mirrors sortLocationsColumn).
    case "m_and_a": return getMAndATagName(lease.locationMAndA || "");
    default: return "";
  }
}

export function applyColumnSort(leases, sortKey, sortDir = 1) {
  if (!sortKey) return leases;
  return [...leases].sort((a, b) => {
    // annual_cost: null annualRentUsd rows land last in both ASC and DESC,
    // regardless of direction. Mirrors the m_and_a null-last pattern.
    if (sortKey === "annual_cost") {
      const aNull = a.annualRentUsd == null;
      const bNull = b.annualRentUsd == null;
      if (aNull && bNull) return String(a.id || "").localeCompare(String(b.id || ""));
      if (aNull) return 1;
      if (bNull) return -1;
    }
    // m_and_a: rows with no tag land last in ASC and first in DESC.
    // The direction-aware sentinel ensures consistent placement regardless of
    // which column is clicked. Checked before the generic comparator so the
    // string comparison never overrides the null-placement rule.
    if (sortKey === "m_and_a") {
      const aEmpty = !a.locationMAndA;
      const bEmpty = !b.locationMAndA;
      if (aEmpty && bEmpty) return String(a.id || "").localeCompare(String(b.id || ""));
      // sortDir 1=ASC: empty last → return 1 (a after b)
      // sortDir -1=DESC: empty first → return -1 (a before b)
      if (aEmpty) return sortDir;
      if (bEmpty) return -sortDir;
    }
    const av = sortValue(a, sortKey);
    const bv = sortValue(b, sortKey);
    if (av < bv) return -sortDir;
    if (av > bv) return sortDir;
    // Universal tiebreak: when two rows compare equal on the primary sort key,
    // resolve by id ascending so the order is always stable and predictable
    // regardless of sort direction or browser sort stability.
    return String(a.id || "").localeCompare(String(b.id || ""));
  });
}
